/* eslint-disable no-underscore-dangle */
import {EventEmitter} from 'events';
import {Subject} from 'rxjs';
import uuid from 'uuid-random';

import {reportPushDeliveryLatency} from '../../../src/services/heartbeat';
import {logger} from '../logger/logger';
import {AblySocket} from './ably';
import {PusherSocket} from './pusher';
import {PushSocket} from './pushSocket';

const COMPARE_EVENT_DELAY_MS = 5000;
const dualPusherAblyLogger = logger.getLogger('dual-pusher-ably');

interface MissedAblyEvent {
  taskId: string;
  eventName: string;
  pusherReceivedAt: Date;
}

interface PushMeta {
  task_id: string;
}

function eventHasPushMeta(value: any): value is {_push_meta: PushMeta} {
  return value && value._push_meta && typeof value._push_meta.task_id === 'string';
}

export class DualPusherAblySocket extends EventEmitter implements PushSocket {
  constructor(
    pusher: PusherSocket,
    ably: AblySocket,
    getHasPusherAblyComparison: () => boolean,
    getHasAblyAuthority: () => boolean,
  ) {
    super();

    this.pusher = pusher;
    this.ably = ably;
    this.getHasPusherAblyComparison = getHasPusherAblyComparison;
    this.getHasAblyAuthority = getHasAblyAuthority;
  }

  private readonly pusher: PusherSocket;
  private readonly ably: AblySocket;
  private getHasPusherAblyComparison: () => boolean;
  private getHasAblyAuthority: () => boolean;
  private ablyMissedEventBuffer: Array<MissedAblyEvent> = [];
  private ablyMissedEventTimeout: number | null = null;

  // Temporarily store events to compare what we receive from Pusher vs Ably.
  private events: Record<string, {pusherReceivedAt?: number; ablyReceivedAt?: number}> = {};

  get isConnected() {
    return this.getHasAblyAuthority() ? this.ably.isConnected : this.pusher.isConnected;
  }

  get isSocketOpen() {
    return this.getHasAblyAuthority() ? this.ably.isSocketOpen : this.pusher.isSocketOpen;
  }

  get id() {
    return this.getHasAblyAuthority() ? this.ably.id : this.pusher.id;
  }

  connect(hasNewPusherOnReconnect: boolean) {
    if (this.isSocketOpen) {
      return false;
    }

    this.registerLifecycleEvents();
    this.registerPresenceEvents();

    this.pusher.connect(hasNewPusherOnReconnect);
    this.ably.connect();

    return true;
  }

  disconnect() {
    if (!this.isSocketOpen) {
      return false;
    }

    this.pusher.disconnect();
    this.ably.disconnect();

    this.unregisterAllEvents();

    return true;
  }

  reconnect(hasNewPusherOnReconnect: boolean) {
    this.pusher.reconnect(hasNewPusherOnReconnect);
    this.ably.reconnect();
  }

  subscribe(roomName: string, roomSubscribes$?: Subject<string>) {
    if (!this.isSocketOpen) {
      throw new Error('Cannot subscribe when not connected.');
    }

    const unsubscribePusher = this.pusher.subscribe(roomName, roomSubscribes$);
    const unsubscribeAbly = this.ably.subscribe(roomName, roomSubscribes$);

    return () => {
      unsubscribePusher();
      unsubscribeAbly();
    };
  }

  onPushEvent(eventName: string, callback: (data: any) => void) {
    if (!this.isSocketOpen) {
      throw new Error('Cannot listen when not connected');
    }

    const deleteCallbackPusher = this.pusher.onPushEvent(eventName, (data: any) =>
      this.onPusherEventCallback(eventName, data, callback),
    );
    const deleteCallbackAbly = this.ably.onPushEvent(eventName, (data: any) =>
      this.onAblyEventCallback(eventName, data, callback),
    );

    return () => {
      deleteCallbackPusher();
      deleteCallbackAbly();
    };
  }

  onDirectPushEvent(eventName: string, callback: (data: any) => void): () => void {
    const deleteCallbackPusher = this.pusher.onDirectPushEvent(eventName, (data: any) =>
      this.onPusherEventCallback(eventName, data, callback),
    );
    const deleteCallbackAbly = this.ably.onDirectPushEvent(eventName, (data: any) =>
      this.onAblyEventCallback(eventName, data, callback),
    );

    return () => {
      deleteCallbackPusher();
      deleteCallbackAbly();
    };
  }

  emitDirectEvent(roomName: string, eventName: string, data: any): void {
    if (!this.isSocketOpen) {
      throw new Error('Cannot emit event when not connected.');
    }

    // Assign a task_id attribute so that this class can compare direct events from Pusher vs Ably.
    const dataWithTaskId = data;
    if (data._push_meta === undefined) {
      dataWithTaskId._push_meta = {
        task_id: `direct-${roomName}-${eventName}-${uuid()}`,
      };
    }

    this.pusher.emitDirectEvent(roomName, eventName, dataWithTaskId);
    this.ably.emitDirectEvent(roomName, eventName, dataWithTaskId);
  }

  ping(): void {
    if (!this.pusher.isSocketOpen) {
      throw new Error('Cannot ping when not connected.');
    }

    this.pusher.ping();
  }

  replaceSubscriptions(subscriptions: string[]) {
    this.ably.replaceSubscriptions(subscriptions);
  }

  // The backend sends a unique task_id event identifier so that the client can here
  // verify that it does receive, at least once, the same events from Pusher and Ably.
  private onPusherEventCallback(eventName: string, data: any, callback: (data: any) => void) {
    const id = eventHasPushMeta(data) ? data._push_meta.task_id : undefined;

    // If the task_id is not set, skip validation.
    if (id) {
      this.events[id] = {
        ...(this.events[id] || {}),
        pusherReceivedAt: performance.now(),
      };

      setTimeout(() => {
        // Pusher doesn't guarantee that events will be broadcasted exactly once to the client.
        // If the event isn't stored anymore, it has already been deleted by another setTimeout callback.
        if (!this.events[id]) {
          return;
        }

        const {ablyReceivedAt, pusherReceivedAt} = this.events[id];

        if (ablyReceivedAt && pusherReceivedAt) {
          reportPushDeliveryLatency({ablyReceivedAt, pusherReceivedAt});
        }

        // Log a warning if Ably never delivered the event.
        if (pusherReceivedAt && ablyReceivedAt === undefined && this.getHasPusherAblyComparison()) {
          this.reportMissedAblyEvent({taskId: id, eventName, pusherReceivedAt: new Date(pusherReceivedAt)});
        }

        delete this.events[id];
      }, COMPARE_EVENT_DELAY_MS);
    }

    if (!this.getHasAblyAuthority()) {
      callback(data);
    }
  }

  private onAblyEventCallback(eventName: string, data: any, callback: (data: any) => void) {
    const id = eventHasPushMeta(data) ? data._push_meta.task_id : undefined;

    if (id) {
      this.events[id] = {
        ...(this.events[id] || {}),
        ablyReceivedAt: performance.now(),
      };

      // Make sure there's no memory leak if Ably is sending events very late or sending events that we don't get from pusher.
      setTimeout(() => {
        delete this.events[id];
      }, COMPARE_EVENT_DELAY_MS * 2);
    }

    if (this.getHasAblyAuthority()) {
      callback(data);
    }
  }

  private registerLifecycleEvents() {
    // Ably.
    this.ably.on(
      'disconnected',
      this.wrapAblyEventHandler((value) => {
        this.emit('disconnected', value);
      }),
    );
    this.ably.on(
      'connected',
      this.wrapAblyEventHandler((value) => {
        this.emit('connected', value);
      }),
    );

    // Pusher.
    this.pusher.on(
      'disconnected',
      this.wrapPusherEventHandler(() => {
        this.emit('disconnected');
      }),
    );
    this.pusher.on(
      'connected',
      this.wrapPusherEventHandler(() => {
        this.emit('connected');
      }),
    );
  }

  private registerPresenceEvents() {
    // Ably.
    this.ably.on(
      'initialPresence',
      this.wrapAblyEventHandler((onlineTeammates) => {
        this.emit('initialPresence', onlineTeammates);
      }),
    );
    this.ably.on(
      'teammateOnline',
      this.wrapAblyEventHandler((onlineTeammate) => {
        this.emit('teammateOnline', onlineTeammate);
      }),
    );
    this.ably.on(
      'teammateOffline',
      this.wrapAblyEventHandler((onlineTeammate) => {
        this.emit('teammateOffline', onlineTeammate);
      }),
    );

    // Pusher.
    this.pusher.on(
      'initialPresence',
      this.wrapPusherEventHandler((onlineTeammates) => {
        this.emit('initialPresence', onlineTeammates);
      }),
    );
    this.pusher.on(
      'teammateOnline',
      this.wrapPusherEventHandler((onlineTeammate) => {
        this.emit('teammateOnline', onlineTeammate);
      }),
    );
    this.pusher.on(
      'teammateOffline',
      this.wrapPusherEventHandler((onlineTeammate) => {
        this.emit('teammateOffline', onlineTeammate);
      }),
    );
  }

  private wrapAblyEventHandler<T>(handler: (value: T) => void) {
    return (value: T) => {
      if (!this.getHasAblyAuthority()) {
        return;
      }

      handler(value);
    };
  }

  private wrapPusherEventHandler<T>(handler: (value: T) => void) {
    return (value: T) => {
      if (this.getHasAblyAuthority()) {
        return;
      }

      handler(value);
    };
  }

  private unregisterAllEvents() {
    this.ably.removeAllListeners();
    this.pusher.removeAllListeners();
  }

  private reportMissedAblyEvent(event: MissedAblyEvent) {
    this.ablyMissedEventBuffer.push(event);

    if (!this.ablyMissedEventTimeout) {
      this.ablyMissedEventTimeout = window.setTimeout(() => {
        dualPusherAblyLogger.warn('Events received by Pusher but missed from Ably', {
          events: JSON.stringify(this.ablyMissedEventBuffer),
        });

        this.ablyMissedEventBuffer = [];
        this.ablyMissedEventTimeout = null;
      }, 5 * 60 * 1000); // 5 minutes
    }
  }
}
